
import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

interface DatabaseConfig {
  id: string;
  name: string;
  type: 'supabase' | 'mysql' | 'postgresql';
  config: {
    // For Supabase
    url?: string;
    anonKey?: string;
    // For MySQL/PostgreSQL
    host?: string;
    port?: number;
    database?: string;
    username?: string;
    password?: string;
  };
  isActive: boolean;
}

export const useDynamicSupabase = () => {
  const [supabaseClient, setSupabaseClient] = useState<any>(null);
  const [currentConfig, setCurrentConfig] = useState<DatabaseConfig | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const loadActiveConfig = () => {
      const activeConfig = localStorage.getItem('active_database_config');
      
      if (activeConfig) {
        const config = JSON.parse(activeConfig);
        setCurrentConfig(config);
        
        // إنشاء عميل قاعدة البيانات حسب النوع
        if (config.type === 'supabase' && config.config.url && config.config.anonKey) {
          const client = createClient<Database>(config.config.url, config.config.anonKey, {
            auth: {
              persistSession: true,
              autoRefreshToken: true,
              detectSessionInUrl: true,
              storage: typeof window !== 'undefined' ? window.localStorage : undefined,
            },
            global: {
              headers: {
                'apikey': config.config.anonKey,
              },
            },
          });
          setSupabaseClient(client);
          setIsReady(true);
        } else if (config.type === 'mysql' || config.type === 'postgresql') {
          // للقواعد التقليدية، نحتاج لإنشاء عميل مخصص
          // هنا يمكن إضافة منطق الاتصال بقواعد البيانات التقليدية
          console.log('التصال بقاعدة بيانات تقليدية:', config);
          // مؤقتاً نضع null للعميل التقليدي
          setSupabaseClient(null);
          setIsReady(false);
        }
      } else {
        // استخدام الإعدادات الافتراضية
        const defaultConfig: DatabaseConfig = {
          id: 'default',
          name: "الإعداد الافتراضي - Supabase",
          type: 'supabase',
          config: {
            url: "https://oensydfsjipboyzupgnm.supabase.co",
            anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9lbnN5ZGZzamlwYm95enVwZ25tIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDk4NDgxNjQsImV4cCI6MjA2NTQyNDE2NH0.THLfPFRmYmJz-OpmEeK_tfMfmdVGVQTPtR59nc73m0M"
          },
          isActive: true
        };
        
        setCurrentConfig(defaultConfig);
        const client = createClient<Database>(defaultConfig.config.url!, defaultConfig.config.anonKey!, {
          auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true,
            storage: typeof window !== 'undefined' ? window.localStorage : undefined,
          },
          global: {
            headers: {
              'apikey': defaultConfig.config.anonKey!,
            },
          },
        });
        setSupabaseClient(client);
        setIsReady(true);
      }
    };

    loadActiveConfig();

    // مراقبة تغييرات localStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'active_database_config') {
        loadActiveConfig();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    supabase: supabaseClient,
    currentConfig,
    isReady: isReady && !!supabaseClient,
    databaseType: currentConfig?.type || 'supabase'
  };
};
